import { FC, useState, useCallback, useRef, useMemo } from 'react';
import Lottie from 'lottie-react';
import { ThumbUp, ThumbDown } from '@ixigo/icons';
import Typography from '@ixigo/iui/Typography';
import likeAnimation from '../../images/Like.json';
import dislikeAnimation from '../../images/Dislike.json';
import { submitNPS } from '../../apis/common';

type FeedbackType = 'like' | 'dislike';

interface NPSCollectorV2Props {
    question: string;
    onFeedback?: (type: FeedbackType) => void;
    bookingId?: string;
    userId?: string;
    campaignId?: string;
    summary?: string;
}

interface ButtonConfig {
    type: FeedbackType;
    icon: typeof ThumbUp | typeof ThumbDown;
    animation: any;
    ref: React.RefObject<any>;
    activeColor: string;
    inactiveColor: string;
}

const NPSCollectorV2: FC<NPSCollectorV2Props> = ({
    question,
    onFeedback,
    bookingId = '',
    userId = '',
    campaignId = '',
    summary = '',
}) => {
    const [feedback, setFeedback] = useState<FeedbackType | null>(null);
    const likeAnimationRef = useRef<any>(null);
    const dislikeAnimationRef = useRef<any>(null);

    // Memoized button configurations
    const buttonConfigs = useMemo<ButtonConfig[]>(
        () => [
            {
                type: 'like',
                icon: ThumbUp,
                animation: likeAnimation,
                ref: likeAnimationRef,
                activeColor: 'text-blue-500',
                inactiveColor: 'text-gray-600',
            },
            {
                type: 'dislike',
                icon: ThumbDown,
                animation: dislikeAnimation,
                ref: dislikeAnimationRef,
                activeColor: 'text-red-500',
                inactiveColor: 'text-gray-600',
            },
        ],
        [],
    );

    // Memoized button class names
    const getButtonClassName = useCallback((buttonType: FeedbackType, currentFeedback: FeedbackType | null) => {
        const baseClasses = 'w-8 h-8 rounded-full border flex items-center justify-center transition-colors';

        if (currentFeedback === buttonType) {
            return `${baseClasses} ${buttonType === 'like' ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`;
        }

        if (currentFeedback !== null) {
            return `${baseClasses} bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed`;
        }

        return `${baseClasses} bg-gray-50 border-gray-200 hover:bg-gray-100`;
    }, []);

    // Memoized metadata creation
    const createMetadata = useCallback(
        (rating: number) => [
            { attribute: 'bookingId', value: bookingId },
            { attribute: 'userId', value: userId },
            { attribute: 'question', value: question },
            { attribute: 'questionId', value: campaignId },
            { attribute: 'language', value: 'en' },
            { attribute: 'summary', value: summary },
            { attribute: 'rating', value: String(rating) },
        ],
        [bookingId, userId, question, campaignId, summary],
    );

    const handleFeedback = useCallback(
        async (type: FeedbackType) => {
            if (feedback !== null) return; // Prevent clicking if already selected

            setFeedback(type);
            onFeedback?.(type);

            // Play animation with proper timing
            setTimeout(() => {
                const config = buttonConfigs.find((c) => c.type === type);
                if (config?.ref.current) {
                    config.ref.current.play();
                }
            }, 100);

            // Submit NPS feedback
            try {
                const rating = type === 'like' ? 5 : 1;
                const metaData = createMetadata(rating);

                await submitNPS(
                    JSON.stringify({
                        userId,
                        campaignId,
                        feedback: {
                            type: 'rating',
                            value: [rating],
                        },
                        metaData,
                        mode: 'live',
                    }),
                );
            } catch (error) {
                // NPS submission error handling
            }
        },
        [feedback, onFeedback, buttonConfigs, createMetadata, campaignId],
    );

    const renderButton = (config: ButtonConfig) => {
        const isActive = feedback === config.type;
        const isDisabled = feedback !== null && !isActive;
        const IconComponent = config.icon;

        return (
            <button
                key={config.type}
                onClick={() => handleFeedback(config.type)}
                disabled={isDisabled}
                className={getButtonClassName(config.type, feedback)}
                type='button'
                aria-label={`${config.type} this answer`}
            >
                {isActive ? (
                    <Lottie
                        lottieRef={config.ref}
                        animationData={config.animation}
                        loop={false}
                        autoplay={false}
                        style={{ width: 24, height: 24 }}
                        className={config.activeColor}
                    />
                ) : (
                    <IconComponent fontSize={24} className={isDisabled ? 'text-gray-400' : config.inactiveColor} />
                )}
            </button>
        );
    };

    return (
        <div className='flex items-center justify-between'>
            <Typography variant='body-sm' className='text-gray-800'>
                {question}
            </Typography>
            <div className='flex items-center gap-2'>{buttonConfigs.map(renderButton)}</div>
        </div>
    );
};

export default NPSCollectorV2;
